// Must run before Amplify initialises: seeds the session from the desktop app's
// signed read-only URL fragment (#chaptery_auth=...) into this tab's sessionStorage, on
// the desktop embed routes only (#1034).
import { configureAuthStorage } from './desktopAuthBootstrap';
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './branding.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Amplify } from 'aws-amplify';
import { initPlausible } from './analytics/plausible';
import { captureAcquisition } from './analytics/acquisition';
import { removeStaticHead } from './seo/removeStaticHead';
import { listenForRedirectFailure } from './auth/redirectFailure';
import { listenForRefreshFailure } from './auth/rememberOnRefreshFailure';

// Load Plausible if configured (no-op without REACT_APP_PLAUSIBLE_DOMAIN).
initPlausible();
// The Reddit pixel is NOT started here. It may load only for signed-out US visitors on
// public marketing pages, which is a routing and auth decision, so AdPixelGate in
// App.tsx makes it. Starting it here loaded it on every route for everyone. (#812)
// Record first-touch acquisition (UTM / referrer) as early as possible.
captureAcquisition();

// Before Amplify.configure, which finishes a Google or Apple sign-in coming back to the
// root and reports a failure once, to whoever is listening by then. (#941)
listenForRedirectFailure();

// Before anything renders, which is where the first token refresh starts: a lapsed
// session notes its page before any sign-out redirect, and App returns to it. (#1039)
listenForRefreshFailure();

// Configure Amplify (Values will be injected during build or from env)
const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN || '';
const redirectUrl = process.env.REACT_APP_REDIRECT_URL || 'http://localhost:3000';

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_USER_POOL_ID || '',
      userPoolClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID || '',
      loginWith: {
        oauth: {
          domain: cognitoDomain,
          scopes: ['openid', 'email', 'profile'],
          redirectSignIn: [redirectUrl],
          redirectSignOut: [redirectUrl],
          responseType: 'code' as const,
        },
      },
    }
  },
  API: {
    REST: {
      ChapteryApi: {
        endpoint: (process.env.REACT_APP_API_ENDPOINT || '').replace(/\/$/, ''),
        region: process.env.REACT_APP_REGION || 'us-east-1'
      }
    }
  }
});

// A desktop embed load reads its session from this tab only, never the saved sign-in,
// so a crafted embed link cannot replace the visitor's own (#1034). After configure,
// which resets the token storage.
configureAuthStorage();

// The build writes each public page's social-card tags into its HTML for crawlers
// that never run JavaScript. This browser is running it, so React owns the head from
// here: drop the static copies before React adds its own, or both sit in the head and
// the static ones go stale on the first client-side navigation. (#814)
removeStaticHead();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

reportWebVitals();
